'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowRight } from 'lucide-react'
import { DecorativeBackground } from '@/components/brand/decorativeBackground'
import { Logo } from '@/components/brand/logo'
import { BrandPanel } from '@/components/brand/brand-panel'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useIsMobile } from '@/hooks/use-mobile'

const ROTATING_WORDS = ['Prescribed.', 'Diagnosed.', 'Optimized.', 'Protected.', 'Simplified.']

export function SplashView() {
  const router = useRouter()
  const isMobile = useIsMobile()
  const [wordIndex, setWordIndex] = useState(0)
  const [swapping, setSwapping] = useState(false)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const interval = setInterval(() => {
      setSwapping(true)
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % ROTATING_WORDS.length)
        setSwapping(false)
      }, 350)
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      {/* Mobile (<768px) — full-bleed video splash */}
      <div className="relative flex h-dvh flex-col overflow-hidden bg-[#080a18] font-[family-name:var(--font-display)] md:hidden">
        <div className="absolute inset-0 z-0">
          <video
            className="absolute inset-0 h-full w-full object-cover opacity-55 mix-blend-luminosity"
            autoPlay
            muted
            loop
            playsInline
          >
            {/* Only mounts the source on an actual mobile viewport — otherwise this (hidden via
                `md:hidden` on the wrapper) and `BrandPanel`'s desktop video would both fetch the
                same file regardless of which is visually shown. */}
            {isMobile && <source src="/video/login-brand-bg.mp4" type="video/mp4" />}
          </video>
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(8,10,24,0.25) 0%, rgba(8,10,24,0.2) 40%, rgba(8,10,24,0.95) 100%), linear-gradient(135deg, rgba(68,76,231,0.35) 0%, rgba(30,36,128,0.2) 60%)',
            }}
          />
        </div>

        <div
          className="pointer-events-none absolute z-0 rounded-full"
          style={{
            width: 560,
            height: 560,
            top: -180,
            left: -180,
            background: 'radial-gradient(circle, rgba(68,76,231,0.32) 0%, transparent 65%)',
          }}
        />
        <div
          className="pointer-events-none absolute z-0 rounded-full"
          style={{
            width: 400,
            height: 400,
            bottom: -120,
            right: -100,
            background: 'radial-gradient(circle, rgba(124,58,237,0.2) 0%, transparent 65%)',
          }}
        />

        <div className="relative z-10 flex items-center gap-2.5 p-7">
          <Logo />
          <span className="text-lg font-bold tracking-tight text-white">Nivex</span>
        </div>

        <div className="relative z-10 flex flex-1 animate-[riseIn_0.8s_cubic-bezier(0.22,1,0.36,1)_both] flex-col justify-end px-7 pb-10">
          <span
            className="mb-5 inline-flex w-fit items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium text-white"
            style={{
              backgroundColor: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <span className="size-1.5 shrink-0 rounded-full bg-blue-600" />
            India&apos;s First Prescription-Based Wealth Platform
          </span>

          <h1 className="mb-3.5 text-[clamp(34px,8vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-white">
            Your
            <br />
            Wealth.{' '}
            <em
              className="inline-block font-medium not-italic text-[#A5B4FC] transition-all duration-[350ms]"
              style={
                swapping
                  ? { opacity: 0, transform: 'translateY(6px)' }
                  : { opacity: 1, transform: 'translateY(0)' }
              }
            >
              {ROTATING_WORDS[wordIndex]}
            </em>
          </h1>

          <p className="mb-8 max-w-[320px] text-[15px] leading-relaxed text-white/50">
            We diagnose your financial health first, then build a personalised wealth prescription —
            not the other way around.
          </p>

          <button
            onClick={() => router.push('/choose-service')}
            className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-[15px] font-semibold text-white transition-colors hover:bg-[#3730cc] focus-visible:ring-4 focus-visible:ring-blue-600/35"
          >
            Get Started
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </button>

          <button
            onClick={() => router.push('/login')}
            className="mt-4 text-center text-[13px] text-white/50"
          >
            Already have an account? <span className="font-semibold text-white">Log in</span>
          </button>
        </div>
      </div>

      {/* Desktop/laptop (≥768px) — split layout, qualifying-first (no login gate) */}
      <div className="hidden min-h-screen bg-[var(--background-secondary,#F8F9FC)] font-[family-name:var(--font-display)] md:flex md:gap-4 md:p-4">
        <BrandPanel />

        <main className="bg-card relative sticky top-4 flex min-w-0 flex-1 basis-0 flex-col items-center justify-center self-start overflow-hidden rounded-2xl shadow-md md:h-[calc(100vh-2rem)]">
          <DecorativeBackground src="/img/checkup-bg.webp" priority sizes="50vw" />
          <div className="bg-card/50 pointer-events-none absolute inset-0 z-0" />
          <div className="relative z-10 w-full max-w-[440px] px-6">
            <Card className="p-4 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-bold tracking-tight">
                  Welcome to Nivex
                </CardTitle>
                <CardDescription className="leading-relaxed">
                  Upload your portfolio and a real financial expert gives you a clear score — not
                  another app to track it yourself.
                </CardDescription>
              </CardHeader>

              <CardFooter className="flex-col items-stretch gap-4 border-t-0 bg-transparent">
                <Button size="lg" onClick={() => router.push('/choose-service')}>
                  Get Started
                  <ArrowRight className="size-4" />
                </Button>

                <p className="text-muted-foreground text-center text-[13px]">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => router.push('/login')}
                    className="text-primary font-semibold underline-offset-4 hover:underline"
                  >
                    Log in
                  </button>
                </p>
              </CardFooter>
            </Card>
          </div>
        </main>
      </div>
    </>
  )
}
